import { Button } from "components/Button";
import { TextField } from "components/Forms/TextField";
import { useViewModel } from "./viewModel";
import { ChatIntegration } from "models/chatIntegration";
import { SaveOrUpdateChatIntegrationFunction } from "../../viewModel";
import { MultiSelect } from "components/Forms/MultiSelect";
import { Select } from "components/Forms/Select";

type Props = {
  provider: string;
  selectedIntegration: ChatIntegration | undefined;
  onClose: () => void;
  onSave: SaveOrUpdateChatIntegrationFunction;
};

export const BlipForm = ({
  selectedIntegration,
  onClose,
  onSave,
  provider,
}: Props) => {
  const viewModel = useViewModel(selectedIntegration, onSave, provider);

  if (viewModel.step === 0) {
    return (
      <form
        onSubmit={viewModel.handleSubmit(viewModel.handleSubmitFormFirstStep)}
        className="flex flex-col gap-5 max-h-96 overflow-y-auto"
      >
        <TextField
          label="Nome da integração"
          placeholder="Digite o nome da integração"
          error={viewModel.errors.name?.message}
          register={viewModel.register("name", {
            required: { value: true, message: "O nome é obrigatório" },
          })}
          variant="secondary"
        />
        <TextField
          label="Domínio Blip"
          placeholder="Digite o seu domínio personalizado do Blip"
          error={viewModel.errors.blip?.host?.message}
          register={viewModel.register("blip.host", {
            required: { value: true, message: "O domínio é obrigatório" },
          })}
          variant="secondary"
        />
        <TextField
          label="Chave de acesso"
          placeholder="Digite o a chave de acesso"
          error={viewModel.errors.blip?.key?.message}
          register={viewModel.register("blip.key", {
            required: { value: true, message: "A chave é obrigatória" },
          })}
          variant="secondary"
          helpText="Essa é a chave de acesso fornecida pelo Blip ao requisitar ao atendimento deles"
        />
        <TextField
          label="Token do Analytics (Opcional)"
          placeholder="Digite o token do Blip Analytics"
          error={viewModel.errors.blip?.analyticsToken?.message}
          register={viewModel.register("blip.analyticsToken")}
          variant="secondary"
          helpText="Token usado para consultar a API de Analytics do Blip e validar a origem dos leads (CTWA). Solicite ao atendimento do Blip"
        />
        <TextField
          label="Departamento (Opcional)"
          placeholder="Digite o departamento"
          error={viewModel.errors.blip?.department?.message}
          register={viewModel.register("blip.department")}
          variant="secondary"
          helpText="Se preenchido a integração buscará apenas lead desse departamento. Porém ainda irá fazer a atribuição de vendedor automática normalmente"
        />
        <TextField
          label="Roteador (Opcional)"
          placeholder="Digite o roteador"
          error={viewModel.errors.blip?.router?.message}
          register={viewModel.register("blip.router")}
          variant="secondary"
          helpText="Esse campo deve ser usado apenas quando o cliente possuir mais de uma conta Blip no mesmo grupo"
        />
        <div className="modalButtons">
          <Button onClick={onClose} theme="cancel">
            Cancelar
          </Button>
          <Button loading={viewModel.loadingForm} theme="highlight">
            Salvar
          </Button>
        </div>
      </form>
    );
  }

  if (viewModel.step === 2) {
    return (
      <form
        onSubmit={viewModel.handleSubmit(viewModel.handleSubmitFormThirdStep)}
        className="flex flex-col gap-5 overflow-y-auto max-h-[calc(100svh-384px)]"
      >
        {viewModel.selectedSellersOptions.map((seller, index) => (
          <div key={seller.id}>
            <Select
              label={`Associe ${seller.name}`}
              onChange={(value) => {
                // Check if the internal seller is already select on the list
                const relatedSeller = viewModel.selectedSellersRelated.find(
                  (relatedSeller) => relatedSeller.seller === value
                );

                // Get current external seller
                const seller = viewModel.selectedSellersOptions[index];

                // Check if the external seller is already select on the list
                const sellerOnTheList = viewModel.selectedSellersRelated.find(
                  (relatedSeller) =>
                    relatedSeller.externalSeller === seller.id
                );

                // If external seller is selected on the list, change the internal seller related to the new selection
                if (sellerOnTheList) {
                  viewModel.setValue("blip.sellersRelated", [
                    ...viewModel.selectedSellersRelated.filter(
                      (relatedSeller) =>
                        relatedSeller.externalSeller !== seller.id
                    ),
                    {
                      seller: value!,
                      externalSeller: seller.id,
                    },
                  ]);
                  return;
                }

                // If the internal seller selected is on the list, change they to the current external seller
                if (relatedSeller) {
                  viewModel.setValue("blip.sellersRelated", [
                    ...viewModel.selectedSellersRelated.filter(
                      (relatedSeller) => relatedSeller.seller !== value!
                    ),
                    {
                      seller: value!,
                      externalSeller: seller.id,
                    },
                  ]);
                  return;
                }

                // If the internal and external seller isn't selected push it
                viewModel.setValue("blip.sellersRelated", [
                  ...viewModel.selectedSellersRelated,
                  {
                    seller: value!,
                    externalSeller: seller.id,
                  },
                ]);
              }}
              options={viewModel.internalSellers.map((internalSeller) => ({
                value: internalSeller.id,
                label: internalSeller.name,
              }))}
              selected={
                viewModel.selectedSellersRelated.find(
                  (sellerRelated) =>
                    sellerRelated.externalSeller === seller.id
                )?.seller
              }
              error={viewModel.errors.type?.message}
              variant="secondary"
            />
          </div>
        ))}
        <div className="modalButtons">
          <Button onClick={onClose} theme="cancel">
            Cancelar
          </Button>
          <Button
            loading={viewModel.loadingForm}
            theme="highlight"
            className="px-7"
          >
            Salvar
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={viewModel.handleSubmit(viewModel.handleSubmitFormSecondStep)}
      className="flex flex-col gap-5"
    >
      <MultiSelect
        label="Selecione os vendedores"
        onChange={(value) => viewModel.setValue("blip.sellers", value)}
        options={viewModel.sellers.sort((a, b) => a.name.localeCompare(b.name)).map((seller) => ({
          value: seller.id,
          label: seller.name,
        }))}
        selected={viewModel.selectedSellers}
        error={viewModel.errors.blip?.sellers?.message}
        variant="secondary"
      />
      <div className="modalButtons">
        <Button onClick={onClose} theme="cancel">
          Cancelar
        </Button>
        <Button loading={viewModel.loadingForm} theme="highlight">
          Salvar
        </Button>
      </div>
    </form>
  );
};
